import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Crown, Users, Search } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const ROLE_LABEL = { broker: "Mediator", owner: "Owner", customer: "Customer", subadmin: "Sub-Admin" };
const errMsg = (e) => formatApiError(e?.response?.data?.detail);

function PlanEditor({ meta }) {
  const [plans, setPlans] = useState(null);
  useEffect(() => { api.get("/admin/plans").then((r) => setPlans(r.data.plans)); }, []);
  const toggle = async (pk, key) => {
    const next = { ...plans, [pk]: { features: { ...plans[pk].features, [key]: !plans[pk].features[key] } } };
    const prev = plans;
    setPlans(next);
    try { const { data } = await api.put("/admin/plans", { plans: next }); setPlans(data.plans); toast.success("Plan updated"); }
    catch (e) { setPlans(prev); toast.error(errMsg(e)); }
  };
  if (!plans) return <p className="text-sm text-brand-muted">Loading plans...</p>;
  return (
    <div className="bg-card border border-brand-line rounded-2xl p-4 sm:p-6 min-w-0" data-testid="plan-editor">
      <h3 className="font-heading font-semibold text-lg flex items-center gap-2 mb-1"><Crown className="w-5 h-5 text-amber-500" /> Plans</h3>
      <p className="text-sm text-brand-muted mb-4">Choose what Free and Paid users get. Turn a feature OFF under Free to make it paid-only.</p>
      <div className="grid grid-cols-[1fr_auto_auto] gap-x-6 gap-y-2 items-center text-sm">
        <span className="font-semibold text-brand-muted text-xs uppercase">Feature</span>
        <span className="font-semibold text-brand-muted text-xs uppercase">Free</span>
        <span className="font-semibold text-brand-muted text-xs uppercase">Paid</span>
        {Object.keys(meta).map((k) => (
          <React.Fragment key={k}>
            <span className="text-brand-ink">{meta[k]}</span>
            <Switch checked={plans.free.features[k]} onCheckedChange={() => toggle("free", k)} data-testid={`plan-free-${k}`} />
            <Switch checked={plans.paid.features[k]} onCheckedChange={() => toggle("paid", k)} data-testid={`plan-paid-${k}`} />
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function UserAccessDialog({ u, meta, onClose, onSaved }) {
  const [plan, setPlan] = useState(u.plan || "free");
  const [expires, setExpires] = useState(u.plan_expires || "");
  const [ov, setOv] = useState(u.feature_overrides || {});
  const setVal = (k, v) => setOv((o) => { const n = { ...o }; if (v === "default") { delete n[k]; } else { n[k] = v === "on"; } return n; });
  const save = async () => {
    try {
      await api.put(`/admin/user-access/${u.id}`, { plan, plan_expires: expires, feature_overrides: ov });
      toast.success("Access saved"); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) { onClose(); } }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="user-access-dialog">
        <DialogHeader><DialogTitle>{u.name || u.email} · Feature Access</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Plan</Label>
            <Select value={plan} onValueChange={setPlan}>
              <SelectTrigger className="mt-1.5" data-testid="access-plan-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="free">Free</SelectItem><SelectItem value="paid">Paid</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label>Paid until (optional)</Label><Input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} className="mt-1.5" data-testid="access-expiry-input" /></div>
        </div>
        <p className="text-xs text-brand-muted mt-2">“Plan default” follows the plan and the global switch. “On” works for this user even if the feature is off globally.</p>
        <div className="space-y-2 mt-2">
          {Object.keys(meta).map((k) => (
            <div key={k} className="flex items-center justify-between gap-3 border border-brand-line rounded-lg px-3 py-2" data-testid={`access-row-${k}`}>
              <span className="text-sm text-brand-ink">{meta[k]}</span>
              <Select value={k in ov ? (ov[k] ? "on" : "off") : "default"} onValueChange={(v) => setVal(k, v)}>
                <SelectTrigger className="w-36 h-8" data-testid={`access-select-${k}`}><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="default">Plan default</SelectItem><SelectItem value="on">On</SelectItem><SelectItem value="off">Off</SelectItem></SelectContent>
              </Select>
            </div>
          ))}
        </div>
        <DialogFooter><Button onClick={save} className="rounded-full bg-brand-emerald" data-testid="access-save-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkBar({ ids, meta, onDone }) {
  const [plan, setPlan] = useState("paid");
  const [expires, setExpires] = useState("");
  const [feat, setFeat] = useState(Object.keys(meta)[0]);
  const [val, setVal] = useState("on");
  const send = async (body) => {
    try {
      const { data } = await api.put("/admin/user-access-bulk", { user_ids: ids, ...body });
      toast.success(`Updated ${data.updated} user(s)`); onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="bg-brand-emerald/5 border border-brand-emerald/30 rounded-xl p-4 space-y-3" data-testid="bulk-bar">
      <p className="text-sm font-semibold text-brand-ink" data-testid="bulk-count">{ids.length} selected</p>
      <div className="flex flex-wrap items-center gap-2">
        <Select value={plan} onValueChange={setPlan}><SelectTrigger className="w-28 h-9" data-testid="bulk-plan-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="free">Free</SelectItem><SelectItem value="paid">Paid</SelectItem></SelectContent></Select>
        <Input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} className="w-40 h-9" data-testid="bulk-expiry-input" />
        <Button size="sm" className="rounded-full bg-brand-emerald" onClick={() => send({ plan, plan_expires: expires })} data-testid="bulk-plan-apply">Set plan</Button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Select value={feat} onValueChange={setFeat}><SelectTrigger className="w-60 h-9" data-testid="bulk-feature-select"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.keys(meta).map((k) => <SelectItem key={k} value={k}>{meta[k]}</SelectItem>)}</SelectContent></Select>
        <Select value={val} onValueChange={setVal}><SelectTrigger className="w-36 h-9" data-testid="bulk-value-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="on">On</SelectItem><SelectItem value="off">Off</SelectItem><SelectItem value="default">Plan default</SelectItem></SelectContent></Select>
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => send({ feature: feat, value: val === "default" ? null : val === "on", clear_feature: val === "default" })} data-testid="bulk-feature-apply">Apply feature</Button>
      </div>
    </div>
  );
}

export const SubscriberAccess = ({ meta }) => {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [sel, setSel] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/user-access").then((r) => setUsers(r.data.users));
  useEffect(() => { load(); }, []);
  const shown = useMemo(() => users.filter((u) => (role === "all" || u.role === role) &&
    `${u.name} ${u.email} ${u.phone}`.toLowerCase().includes(q.toLowerCase())), [users, q, role]);
  const toggleSel = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSel = shown.length > 0 && shown.every((u) => sel.includes(u.id));
  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_420px] gap-6 min-w-0" data-testid="subscriber-access">
      <div className="space-y-4 min-w-0">
        <div className="bg-card border border-brand-line rounded-2xl p-4 sm:p-6">
          <h3 className="font-heading font-semibold text-lg flex items-center gap-2 mb-1"><Users className="w-5 h-5 text-brand-emerald" /> Users</h3>
          <p className="text-sm text-brand-muted mb-4">Set a plan or switch features for one user, or tick several and update them together.</p>
          <div className="flex flex-wrap gap-2 mb-4">
            <div className="relative flex-1 min-w-[180px]"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone" className="pl-9" data-testid="access-search" /></div>
            <Select value={role} onValueChange={setRole}><SelectTrigger className="w-40" data-testid="access-role-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All roles</SelectItem>{Object.keys(ROLE_LABEL).map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>)}</SelectContent></Select>
          </div>
          {sel.length > 0 && <div className="mb-4"><BulkBar ids={sel} meta={meta} onDone={() => { setSel([]); load(); }} /></div>}
          <label className="flex items-center gap-2 text-sm text-brand-muted mb-2"><Checkbox checked={allSel} onCheckedChange={() => setSel(allSel ? [] : shown.map((u) => u.id))} data-testid="access-select-all" /> Select all shown ({shown.length})</label>
          <div className="space-y-2">
            {shown.map((u) => (
              <div key={u.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 border border-brand-line rounded-xl px-3 py-2.5" data-testid={`access-user-${u.id}`}>
                <Checkbox checked={sel.includes(u.id)} onCheckedChange={() => toggleSel(u.id)} data-testid={`access-check-${u.id}`} />
                <div className="flex-1 min-w-[140px]">
                  <p className="text-sm font-medium text-brand-ink truncate">{u.name || u.email}</p>
                  <p className="text-xs text-brand-muted truncate">{ROLE_LABEL[u.role] || u.role} · {u.email}</p>
                </div>
                <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${u.active_plan === "paid" ? "bg-amber-100 text-amber-700" : "bg-brand-muted/10 text-brand-muted"}`} data-testid={`access-plan-${u.id}`}>
                  {u.active_plan === "paid" ? "Paid" : u.plan === "paid" ? "Expired" : "Free"}{u.plan_expires ? ` · ${u.plan_expires}` : ""}
                </span>
                {Object.keys(u.feature_overrides).length > 0 && <span className="text-[11px] text-brand-emerald font-semibold">{Object.keys(u.feature_overrides).length} custom</span>}
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => setEditing(u)} data-testid={`access-manage-${u.id}`}>Manage</Button>
              </div>
            ))}
            {shown.length === 0 && <p className="text-sm text-brand-muted py-6 text-center">No users found.</p>}
          </div>
        </div>
      </div>
      <PlanEditor meta={meta} />
      {editing && <UserAccessDialog u={editing} meta={meta} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
};
